import type { VenueOperatingHours } from "@clutch/auth/actions/venues";
import { DAY_LABELS, DAYS_OF_WEEK, type DayOfWeek } from "./constants";

type OpenStatus = {
	isOpen: boolean;
	label: string;
};

function parseHhmm(value: string): number | null {
	const m = value.match(/^(\d{1,2}):(\d{2})$/);
	if (!m) return null;
	const h = Number(m[1]);
	const min = Number(m[2]);
	if (Number.isNaN(h) || Number.isNaN(min)) return null;
	return h * 60 + min;
}

function formatHhmm(value: string): string {
	const m = value.match(/^(\d{1,2}):(\d{2})$/);
	if (!m) return value;
	const h = Number(m[1]);
	const min = Number(m[2]);
	const period = h >= 12 ? "PM" : "AM";
	const h12 = h % 12 === 0 ? 12 : h % 12;
	return min === 0
		? `${h12} ${period}`
		: `${h12}:${String(min).padStart(2, "0")} ${period}`;
}

export function getOpenStatus(
	operatingHours: VenueOperatingHours | null,
	now: Date = new Date(),
): OpenStatus | null {
	if (!operatingHours) return null;

	const jsDay = now.getDay(); // 0=Sun..6=Sat
	const todayIdx = (jsDay + 6) % 7; // 0=Mon..6=Sun
	const today = DAYS_OF_WEEK[todayIdx];
	const nowMinutes = now.getHours() * 60 + now.getMinutes();

	const todayHours = operatingHours[today];
	if (todayHours) {
		const open = parseHhmm(todayHours.open);
		const close = parseHhmm(todayHours.close);
		if (open !== null && close !== null) {
			const isOvernight = close <= open;
			const openNow = isOvernight
				? nowMinutes >= open || nowMinutes < close
				: nowMinutes >= open && nowMinutes < close;
			if (openNow) {
				return {
					isOpen: true,
					label: `Open · closes ${formatHhmm(todayHours.close)}`,
				};
			}
			if (nowMinutes < open) {
				return {
					isOpen: false,
					label: `Closed · opens ${formatHhmm(todayHours.open)}`,
				};
			}
		}
	}

	for (let i = 1; i <= 7; i += 1) {
		const nextIdx = (todayIdx + i) % 7;
		const nextDay = DAYS_OF_WEEK[nextIdx] as DayOfWeek;
		const nextHours = operatingHours[nextDay];
		if (nextHours) {
			const dayLabel = i === 1 ? "tomorrow" : DAY_LABELS[nextDay];
			return {
				isOpen: false,
				label: `Closed · opens ${formatHhmm(nextHours.open)} ${dayLabel}`,
			};
		}
	}

	return { isOpen: false, label: "Closed" };
}
